"use client";

import Link from "next/link";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, KeyRound } from "lucide-react";
import { trackClientEvent } from "@/lib/analyticsClient";

/** QW Intelligence "liquid metal" text: cold silver with an emerald tail. */
export const metalText: React.CSSProperties = {
  background: "linear-gradient(100deg, #f2f4f3, #9aa09e 40%, #e8eae9 55%, #7fe7c8 130%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

/**
 * Dark shell sharing the homepage hero backdrop language: concentric
 * cyan-to-emerald arcs top-right, an emerald glow orb bottom-left, a subtle
 * dot grid, plus the QW Intelligence silver sheen up top.
 */
export function DevLandingShell({ children }: { children: React.ReactNode }) {
  return (
    <section className="relative overflow-hidden bg-[#0a0a0b] pb-16 pt-20 sm:pb-24 sm:pt-24">
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <svg
          className="pointer-events-none absolute -right-20 -top-20 h-[500px] w-[500px] opacity-30 md:h-[700px] md:w-[700px]"
          viewBox="0 0 700 700"
          fill="none"
        >
          <defs>
            <linearGradient id="devArcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#10b981" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M700 0 A700 700 0 0 0 0 700" stroke="url(#devArcGrad)" strokeWidth="1" />
          <path d="M700 80 A620 620 0 0 0 80 700" stroke="url(#devArcGrad)" strokeWidth="0.8" />
          <path d="M700 160 A540 540 0 0 0 160 700" stroke="url(#devArcGrad)" strokeWidth="0.6" />
          <path d="M700 240 A460 460 0 0 0 240 700" stroke="url(#devArcGrad)" strokeWidth="0.5" />
          <path d="M700 320 A380 380 0 0 0 320 700" stroke="url(#devArcGrad)" strokeWidth="0.4" />
        </svg>
        <div
          className="absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full opacity-25"
          style={{
            background:
              "radial-gradient(ellipse, rgba(154,160,158,0.25) 0%, rgba(154,160,158,0.06) 45%, transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0.08) 40%, transparent 70%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>
      <MaxWidthWrapper className="relative z-10">{children}</MaxWidthWrapper>
    </section>
  );
}

/** Pulsing emerald dot used next to "real-time" / live labels. */
export function LiveDot({ className = "" }: { className?: string }) {
  return (
    <span className={`relative flex h-2 w-2 ${className}`} aria-hidden="true">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
    </span>
  );
}

/** Graphite hero pill, e.g. "REST API - live now". */
export function HeroBadge({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-[#3a3c3b] bg-gradient-to-b from-[#1c1e1d] to-[#131414] px-4 py-1.5">
      {children}
    </div>
  );
}

interface GetKeyButtonProps {
  keyHref: string;
  eventName: string;
  location?: string;
  className?: string;
}

/** The one green-gradient primary CTA: "Get your key". */
export function GetKeyButton({ keyHref, eventName, location, className = "" }: GetKeyButtonProps) {
  return (
    <Link href={keyHref} className={className || "w-full sm:w-auto"}>
      <Button
        size="lg"
        className="h-12 w-full shrink-0 gap-2 whitespace-nowrap rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 px-8 text-base text-white shadow-sm hover:opacity-90 sm:w-auto"
        onClick={() =>
          trackClientEvent({ name: eventName, params: { href: keyHref, ...(location ? { location } : {}) } })
        }
      >
        <KeyRound className="h-4 w-4" />
        Get your key
        <ArrowRight className="h-4 w-4" />
      </Button>
    </Link>
  );
}

/** Quiet outline secondary CTA next to the green one. */
export function SecondaryCta({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) {
  const button = (
    <Button
      size="lg"
      variant="outline"
      className="h-12 w-full shrink-0 gap-2 whitespace-nowrap rounded-lg border-white/20 bg-transparent px-8 text-base text-white hover:border-white/40 hover:bg-white/10 sm:w-auto"
    >
      {children}
    </Button>
  );
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
        {button}
      </a>
    );
  }
  return (
    <Link href={href} className="w-full sm:w-auto">
      {button}
    </Link>
  );
}

/** Centered section heading with optional metal accent handled by the caller. */
export function SectionHeading({ title, sub }: { title: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="text-center">
      <h2 className="text-3xl tracking-tight text-white">{title}</h2>
      {sub ? <p className="mx-auto mt-3 max-w-2xl text-balance text-white/60">{sub}</p> : null}
    </div>
  );
}

export interface Stat {
  value: string;
  label: string;
}

/** Borderless stat band: liquid-metal numbers separated by hairlines. */
export function StatsRow({ stats }: { stats: Stat[] }) {
  return (
    <div className="mx-auto mt-14 grid max-w-3xl grid-cols-2 gap-y-8 sm:grid-cols-4">
      {stats.map((stat, i) => (
        <div
          key={stat.label}
          className={`flex flex-col items-center gap-1.5 px-4 ${
            i % 2 === 1 ? "border-l border-white/[0.08]" : ""
          } ${i === 2 ? "sm:border-l sm:border-white/[0.08]" : ""}`}
        >
          <span className="text-3xl tracking-tight" style={metalText}>
            {stat.value}
          </span>
          <span className="text-center text-xs uppercase tracking-[0.14em] text-[#8d918f]">{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

interface FinalCtaProps {
  title: React.ReactNode;
  sub: React.ReactNode;
  keyHref: string;
  eventName: string;
  note?: string;
}

/** Closing CTA block reused across all dev landing pages. */
export function FinalCta({ title, sub, keyHref, eventName, note }: FinalCtaProps) {
  return (
    <div className="relative mt-24 sm:mt-32">
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-[320px] w-[640px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25"
        style={{ background: "radial-gradient(ellipse, rgba(16,185,129,0.35) 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative text-center">
        <h2 className="mx-auto max-w-2xl text-balance text-3xl tracking-tight text-white sm:text-4xl">{title}</h2>
        <p className="mx-auto mt-4 max-w-xl text-balance text-white/60">{sub}</p>
        <div className="mt-8 flex justify-center">
          <GetKeyButton keyHref={keyHref} eventName={eventName} location="footer" className="" />
        </div>
        {note ? <p className="mt-4 text-xs text-white/50 sm:text-sm">{note}</p> : null}
      </div>
    </div>
  );
}

export interface Step {
  title: string;
  description: React.ReactNode;
}

/** "Up and running in minutes" rail: numbered steps on a hairline, no box. */
export function StepsCard({ title, steps }: { title: React.ReactNode; steps: Step[] }) {
  return (
    <div className="mt-20 sm:mt-24">
      <h2 className="text-center text-2xl tracking-tight text-white sm:text-3xl">{title}</h2>
      <div className="mt-10 grid gap-8 md:grid-cols-3 md:gap-10">
        {steps.map((step, i) => (
          <div key={step.title} className="relative border-t border-white/[0.08] pt-6">
            <span
              className="absolute -top-px left-0 h-px w-12 bg-gradient-to-r from-emerald-400/80 to-transparent"
              aria-hidden="true"
            />
            <span className="font-mono text-xs text-[#7fe7c8]">0{i + 1}</span>
            <h3 className="mt-2 text-white">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-white/60">{step.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CrossSellProps {
  badge: string;
  title: React.ReactNode;
  description: React.ReactNode;
  href: string;
  linkLabel: string;
  className?: string;
}

/** Slim band cross-selling the sibling API/MCP page. */
export function CrossSellBand({
  badge,
  title,
  description,
  href,
  linkLabel,
  className = "mt-20 sm:mt-24",
}: CrossSellProps) {
  return (
    <Card className={`border-[#1f2020] bg-[#101111] p-8 ${className}`}>
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="max-w-xl">
          <span className="text-xs uppercase tracking-wider text-[#8d918f]">{badge}</span>
          <h3 className="mt-2 text-xl tracking-tight text-white">{title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-white/60">{description}</p>
        </div>
        <Link href={href} className="shrink-0">
          <Button
            variant="outline"
            className="h-11 shrink-0 gap-2 whitespace-nowrap rounded-lg border-[#3a3c3b] bg-gradient-to-b from-[#1c1e1d] to-[#131414] px-6 text-[#e8eae9] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] hover:border-[#4a4c4b]"
          >
            {linkLabel}
            <ArrowRight className="h-4 w-4 text-[#9aa09e]" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}
